import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from "react";
import { createContext, useReducer, useMemo, useRef, useEffect, useCallback } from "react";
import classnames from "classnames";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { useRouter } from "next/router";
import { useModal } from "@/hooks";
import isOuterAreaClick from "@/lib/isOuterAreaClick";
import styles from "@/styles/Modal.module.scss";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var IconClose = function IconClose(props) {
  return /*#__PURE__*/_jsxs("svg", _objectSpread(_objectSpread({}, props), {}, {
    children: [/*#__PURE__*/_jsx("rect", {
      width: "48",
      height: "48",
      fill: "rgba(255,255,255,0)"
    }), /*#__PURE__*/_jsx("path", {
      d: "M12,11.293,22.293,1,23,1.707,12.707,12,23,22.293,22.293,23,12,12.707,1.707,23,1,22.293,11.293,12,1,1.707,1.707,1Z",
      transform: "translate(12 12)",
      fill: "#909090",
      fillRule: "evenodd"
    })]
  }));
};
IconClose.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  width: "48",
  height: "48",
  viewBox: "0 0 48 48"
};
export const ModalContext = /*#__PURE__*/createContext({
  showModal: () => null,
  hideModal: () => null
});
ModalContext.displayName = "ModalContext";
let id = 0;
function generateId() {
  return id++;
}
const initialValue = [];
const reducer = (state, action) => {
  const newState = [...state];
  switch (action.type) {
    case "add":
      newState.push(action.value);
      return newState;
    case "remove":
      newState.pop();
      return newState;
    default:
      return state;
  }
};
export function ModalProvider(_ref) {
  let {
    children
  } = _ref;
  const {
    0: modals,
    1: dispatch
  } = useReducer(reducer, initialValue);
  const showModal = useCallback(function (modalComponent) {
    let modalProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    dispatch({
      type: "add",
      value: {
        component: modalComponent,
        props: modalProps,
        key: `modal-${generateId()}`
      }
    });
  }, []);
  const hideModal = useCallback(() => {
    dispatch({
      type: "remove"
    });
  }, []);
  const contextValue = useMemo(() => ({
    showModal,
    hideModal
  }), []);
  return /*#__PURE__*/_jsxs(ModalContext.Provider, {
    value: contextValue,
    children: [children, modals.map(modal => /*#__PURE__*/_jsx(Modal, _objectSpread(_objectSpread({}, modal.props), {}, {
      children: modal.component
    }), modal.key))]
  });
}
export default function Modal(_ref2) {
  let {
    style = "medium",
    closeButton = true,
    outerAreaClose = true,
    onClose,
    children
  } = _ref2;
  const modalStyles = useMemo(() => {
    if (typeof style === "string") {
      return styles[style];
    }
    if (Array.isArray(style)) {
      return style.map(s => {
        return styles[s];
      }).join(" ");
    }
  }, [style]);
  const screen = useRef();
  const content = useRef();
  const {
    hideModal
  } = useModal();
  const router = useRouter();
  useEffect(() => {
    disableBodyScroll(screen.current, {
      allowTouchMove: el => {
        return el.tagName === "DIV" || el.tagName === "SPAN";
      }
    });
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);
  const close = useCallback(e => {
    if (onClose) {
      onClose(e);
    }
    enableBodyScroll(screen.current);
    hideModal();
  }, []);
  const onScreenClick = useCallback(e => {
    if (!outerAreaClose) return false;
    if (isOuterAreaClick(content.current, e)) {
      close(e);
    }
  });
  useEffect(() => {
    router.events.on("routeChangeComplete", close);
    return () => {
      router.events.off("routeChangeComplete", close);
    };
  }, [close]);
  return /*#__PURE__*/_jsx("div", {
    className: styles.modal,
    ref: screen,
    onClick: onScreenClick,
    children: /*#__PURE__*/_jsxs("div", {
      className: classnames(styles.content, modalStyles),
      ref: content,
      children: [closeButton && /*#__PURE__*/_jsx("button", {
        className: styles.close,
        tabIndex: "0",
        role: "button",
        onClick: close,
        children: /*#__PURE__*/_jsx(IconClose, {})
      }), children]
    })
  });
}